import { OcpiAdminApi } from './ocpi-admin-api';
import { TokenManager } from '@/core/auth/token-manager';
import {
  ProtocolConnection,
  OcpiPartyDto,
  PublishLocationsResponse,
  PublishTariffResponse,
  ExternalCpoConnectionRequestDto,
  VpostConnectionRequestDto,
  CpoType,
  CpoSyncOutcome,
  csmsToCpoType,
} from '../types/api-types';

/**
 * Result of detecting operator CPO type
 */
export type OperatorTypeResult = CpoType | 'not_configured';

/**
 * Result of getting OCPI overview - includes type, ocpiId, and connections
 */
export interface OcpiOverviewResult {
  cpoType: CpoType;
  ocpiId: {
    partyId: string;
    countryCode: string | null;
  };
  connections: ProtocolConnection[];
}

/**
 * Domain model for connection data from OCPI Admin API
 */
export interface ConnectionData {
  connections: ProtocolConnection[];
  ocpiConnection: ProtocolConnection | null;
  kioskConnection: ProtocolConnection | null;
  hasOcpiConnection: boolean;
  hasKioskConnection: boolean;
}

/**
 * Repository for OCPI Admin data operations
 * Unified repository replacing separate OcpiCpoRepository and KioskOperatorRepository
 * Handles business logic and transformation between API and domain models
 */
export class OcpiAdminRepository {
  constructor(
    private readonly api: OcpiAdminApi,
    private readonly tokenManager: TokenManager
  ) {}

  /**
   * Get OCPI overview for an operator using the unified endpoint
   * This replaces the complex detectOperatorType() logic with a single API call
   *
   * @param operatorId - Nayax operator ID
   * @returns OcpiOverviewResult with cpoType, ocpiId, and connections
   * @returns null if operator is not configured (404)
   */
  async getOcpiOverview(operatorId: number): Promise<OcpiOverviewResult | null> {
    try {
      const overview = await this.api.getOcpiOverview(operatorId);

      // External CPO uses ptp_connections; Internal CPO uses connections
      const connections = overview.ptp_connections ?? overview.connections ?? [];

      return {
        cpoType: csmsToCpoType(overview.csms),
        // ocpi_id is only present for Internal CPO operators
        ocpiId: overview.ocpi_id
          ? {
              partyId: overview.ocpi_id.partyId,
              countryCode: overview.ocpi_id.countryCode,
            }
          : undefined,
        connections,
      };
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : '';

      // 404 means operator is not configured for OCPI - needs onboarding
      // Only match on actual HTTP 404 status — NOT on message content which
      // can also appear in 500 responses ("Failed to get CPO OCPI overview details")
      const is404 =
        errorMessage.includes('404') ||
        errorMessage.toLowerCase().includes('not found');

      if (is404) {
        return null;
      }

      // Re-throw other errors (including 500)
      console.error('Error fetching OCPI overview:', error);
      throw error; // Re-throw original error to preserve full details
    }
  }

  /**
   * Get all connections for an operator
   * Returns structured data with separated OCPI and Kiosk connections
   *
   * @deprecated Use getOcpiOverview() instead - single API call with CSMS type
   * @param operatorId - Nayax operator ID
   * @returns ConnectionData with parsed connections or null if not configured
   */
  async getConnections(operatorId: number): Promise<ConnectionData | null> {
    try {
      const connections = await this.api.getConnections(operatorId);
      return this.transformConnectionsToData(connections);
    } catch (error: unknown) {
      // Always throw the error with the original message so the store can analyze it
      // The store will check for "Infuse operator ID" to determine if onboarding is needed
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      console.error('Error fetching OCPI-Admin connections:', error);
      throw new Error(`Failed to fetch connections: ${errorMessage}`);
    }
  }

  /**
   * Setup or update Kiosk connection for an operator
   *
   * @param operatorId - Nayax operator ID
   */
  async setupKioskConnection(operatorId: number): Promise<void> {
    try {
      await this.api.setupKioskConnection(operatorId);
    } catch (error) {
      console.error('Error setting up Kiosk connection:', error);
      throw new Error(
        `Failed to setup Kiosk connection: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Update CPO party information
   *
   * @param operatorId - Nayax operator ID
   * @param data - OCPI party data
   */
  async updateCpoParty(operatorId: number, data: OcpiPartyDto): Promise<void> {
    try {
      await this.api.updateCpoParty(operatorId, data);
    } catch (error) {
      console.error('Error updating CPO party:', error);
      throw new Error(
        `Failed to update CPO party: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Publish locations to OCPI network
   *
   * @param operatorId - Nayax operator ID
   * @returns Publish result with machine-level details
   */
  async publishLocations(operatorId: number): Promise<PublishLocationsResponse> {
    try {
      return await this.api.publishLocations(operatorId);
    } catch (error) {
      console.error('Error publishing locations:', error);
      throw this.withHttpStatus('Failed to publish locations', error);
    }
  }

  /**
   * Publish tariffs to OCPI network
   *
   * @param operatorId - Nayax operator ID
   * @returns Publish result
   */
  async publishTariffs(operatorId: number): Promise<PublishTariffResponse> {
    try {
      return await this.api.publishTariffs(operatorId);
    } catch (error) {
      console.error('Error publishing tariffs:', error);
      throw this.withHttpStatus('Failed to publish tariffs', error);
    }
  }

  /**
   * Setup external CPO connection for an operator
   *
   * @param operatorId - Nayax operator ID
   * @param data - External CPO connection configuration
   */
  async setupExternalCpoConnection(
    operatorId: number,
    data: ExternalCpoConnectionRequestDto
  ): Promise<void> {
    try {
      await this.api.setupExternalCpoConnection(operatorId, data);
    } catch (error) {
      console.error('Error setting up external CPO connection:', error);
      throw new Error(
        `Failed to setup external CPO connection: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get available CSMS names for OCPI-to-VPOST connection
   *
   * @returns Array of CSMS name strings
   */
  async getCsmsNames(): Promise<string[]> {
    try {
      const result = await this.api.getCsmsNames();
      return result ?? [];
    } catch (error) {
      console.error('Error fetching CSMS names:', error);
      throw new Error(
        `Failed to fetch CSMS names: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Get available CSMS names for Kiosk connection setup
   *
   * @returns Array of CSMS name strings
   */
  async getKioskCsmsNames(): Promise<string[]> {
    try {
      const result = await this.api.getKioskCsmsNames();
      return result ?? [];
    } catch (error) {
      console.error('Error fetching Kiosk CSMS names:', error);
      throw new Error(
        `Failed to fetch Kiosk CSMS names: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Setup OCPI-to-VPOST connection for an external CPO operator
   *
   * @param operatorId - Nayax operator ID
   * @param data - VPOST connection configuration (includes csms_name)
   */
  async setupVpostConnection(
    operatorId: number,
    data: VpostConnectionRequestDto
  ): Promise<void> {
    try {
      await this.api.setupVpostConnection(operatorId, data);
    } catch (error) {
      console.error('Error setting up VPOST connection:', error);
      throw new Error(
        `Failed to setup OCPI-to-VPOST connection: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Detect operator CPO type (internal, external, or not configured)
   *
   * @deprecated Use getOcpiOverview() instead - single API call that returns CSMS type
   *
   * Detection logic:
   * 1. GET /connections/nayax-kiosk - get all kiosk connections
   * 2. GET /internal-ocpi-cpo/{id}/connections - get internal connections
   *
   * Result interpretation:
   * - If internal returns 200 → Internal CPO
   * - If internal returns 404 but operator is in kiosk connections → External CPO
   * - If neither has the operator → Not configured (needs onboarding)
   *
   * @param operatorId - Nayax operator ID
   * @returns 'internal', 'external', or 'not_configured'
   */
  async detectOperatorType(operatorId: number): Promise<OperatorTypeResult> {
    try {
      // First, get all kiosk connections to check if operator exists there
      let hasKioskConnection = false;
      try {
        const allKioskConnections = await this.api.getAllKioskConnections();
        // Check if operator ID is in the kiosk connections
        // The kiosk connections contain operator info - need to check structure
        hasKioskConnection = allKioskConnections && allKioskConnections.length > 0;
      } catch {
        // If getAllKioskConnections fails, assume no kiosk connection
        hasKioskConnection = false;
      }

      // Try to get internal connections
      try {
        const connections = await this.api.getConnections(operatorId);
        // If we get connections (even empty array), it's internal
        if (connections !== undefined) {
          return 'internal';
        }
      } catch (error) {
        // 404 means operator not configured as internal
        const is404 = error instanceof Error &&
          (error.message.includes('404') || error.message.includes('not found'));

        if (is404 && hasKioskConnection) {
          // Operator is in kiosk connections but not internal → External CPO
          return 'external';
        }

        // If internal returns 404 and no kiosk connection → not configured
        if (is404) {
          return 'not_configured';
        }

        // Re-throw other errors
        throw error;
      }

      // Default to not configured
      return 'not_configured';
    } catch (error) {
      console.error('Error detecting operator type:', error);
      throw new Error(
        `Failed to detect operator type: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Check if operator has any connections configured
   *
   * @param operatorId - Nayax operator ID
   * @returns true if operator has at least one connection
   */
  async isOperatorConfigured(operatorId: number): Promise<boolean> {
    const connectionData = await this.getConnections(operatorId);
    return connectionData !== null && connectionData.connections.length > 0;
  }

  /**
   * Transform raw connections array to structured ConnectionData
   */
  private transformConnectionsToData(connections: ProtocolConnection[]): ConnectionData {
    // Find OCPI connection (type = 'OCPI')
    const ocpiConnection = connections.find(c => c.type === 'OCPI') || null;

    // For Kiosk, we check if there's a registered OCPI connection
    // The Kiosk connection is represented by a registered OCPI connection
    const kioskConnection = connections.find(c => c.type === 'OCPI' && c.registered) || null;

    return {
      connections,
      ocpiConnection,
      kioskConnection,
      hasOcpiConnection: ocpiConnection !== null,
      hasKioskConnection: kioskConnection !== null && kioskConnection.registered,
    };
  }

  /**
   * Sync latest CPO OCPI data for a connection and module (External CPO only)
   *
   * @param operatorId - Nayax operator ID
   * @param connectionId - Connection ID
   * @param ocpiModule - OCPI module: locations | tariffs | sessions | cdrs
   */
  async syncCpoOcpiData(
    operatorId: number,
    connectionId: number,
    ocpiModule: 'locations' | 'tariffs' | 'sessions' | 'cdrs'
  ): Promise<CpoSyncOutcome> {
    try {
      return await this.api.syncCpoOcpiData(operatorId, connectionId, ocpiModule);
    } catch (error) {
      console.error('Error syncing CPO OCPI data:', error);
      throw this.withHttpStatus(`Failed to sync ${ocpiModule}`, error);
    }
  }

  /**
   * Wrap a failure with context, keeping the HTTP status so alerts can explain it (e.g. timeout).
   */
  private withHttpStatus(context: string, error: unknown): Error & { status?: number } {
    const wrapped: Error & { status?: number } = new Error(
      `${context}: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
    wrapped.status = (error as { status?: number } | null)?.status;
    return wrapped;
  }

  /**
   * Sync EVSEs (Electric Vehicle Supply Equipment) data
   *
   * @param operatorId - Nayax operator ID
   * @param publishToOcpi - Whether to publish to OCPI after sync (default: true)
   */
  async syncEvses(operatorId: number, publishToOcpi: boolean = true): Promise<void> {
    try {
      await this.api.syncEvses(operatorId, publishToOcpi);
    } catch (error) {
      console.error('Error syncing EVSEs:', error);
      throw this.withHttpStatus('Failed to sync EVSEs', error);
    }
  }

  /**
   * Check if the current user has the required authentication token
   */
  async isAuthenticated(): Promise<boolean> {
    try {
      const authHeaders = await this.tokenManager.getAuthHeaders();
      const hasOperatorId =
        authHeaders['X-Operator-Id'] && authHeaders['X-Operator-Id'] !== 'unknown';
      const hasAuthHeader = !!authHeaders['authorization'];

      return hasOperatorId && hasAuthHeader;
    } catch (error) {
      console.error('Authentication check failed:', error);
      return false;
    }
  }
}
